import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';

export interface ScenarioItem {
    index: number;
    name: string;
    status: 'pending' | 'running' | 'passed' | 'failed';
}

export interface BatchItem {
    batch_index: number;
    batch_name: string;
    scenarios: ScenarioItem[];
}

type PopoverPosition = {
    top: number;
    left: number;
    arrowOffset: number;
    arrowPosition: 'left' | 'right';
};

interface TestPlanPopoverProps {
    isOpen: boolean;
    onClose: () => void;
    triggerRef: React.RefObject<HTMLButtonElement>;
    isLive: boolean;
    testPlan: BatchItem[];
    scenarios: ScenarioItem[];
    currentBatchIndex: number;
}

export const TestPlanPopover: React.FC<TestPlanPopoverProps> = ({
    isOpen,
    onClose,
    triggerRef,
    isLive,
    testPlan,
    scenarios,
    currentBatchIndex,
}) => {
    const popoverRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<PopoverPosition | null>(null);

    const hasTestPlan = testPlan.length > 0;
    const hasScenarios = scenarios.length > 0;

    // Calculate position and handle scroll
    useEffect(() => {
        if (!isOpen || !triggerRef.current || !popoverRef.current) return;

        const calculatePosition = () => {
            const triggerRect = triggerRef.current!.getBoundingClientRect();
            const popoverRect = popoverRef.current!.getBoundingClientRect();
            
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            const SPACING = 4;
            const ARROW_SIZE = 6;
            
            // Check if trigger is out of viewport - if so, close the popover
            const isTriggerOutOfViewport = 
                triggerRect.bottom < 0 || 
                triggerRect.top > viewportHeight ||
                triggerRect.right < 0 || 
                triggerRect.left > viewportWidth;
            
            if (isTriggerOutOfViewport) {
                onClose();
                return;
            }
            
            // Try positioning to the right first
            let left = triggerRect.right + SPACING + ARROW_SIZE;
            let top = triggerRect.top + (triggerRect.height / 2) - (popoverRect.height / 2);
            let arrowOffset = popoverRect.height / 2;
            let arrowPosition: 'left' | 'right' = 'left'; // Arrow on left means popover is on right
            
            // If no space on right, position to the left
            if (left + popoverRect.width > viewportWidth - SPACING) {
                left = triggerRect.left - SPACING - popoverRect.width - ARROW_SIZE;
                arrowPosition = 'right'; // Arrow on right means popover is on left
            }
            
            // Adjust if popover goes off top
            if (top < SPACING) {
                arrowOffset = arrowOffset + top - SPACING;
                top = SPACING;
            }
            // Adjust if popover goes off bottom
            else if (top + popoverRect.height > viewportHeight - SPACING) {
                arrowOffset = arrowOffset + (top + popoverRect.height - (viewportHeight - SPACING));
                top = viewportHeight - SPACING - popoverRect.height;
            }

            setPosition({ top, left, arrowOffset, arrowPosition });
        };

        // Initial calculation
        calculatePosition();

        // Recalculate on scroll or resize
        window.addEventListener('scroll', calculatePosition, true);
        window.addEventListener('resize', calculatePosition);

        return () => {
            window.removeEventListener('scroll', calculatePosition, true);
            window.removeEventListener('resize', calculatePosition);
        };
    }, [isOpen, triggerRef, onClose]);

    // Handle click outside to close
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                popoverRef.current && 
                !popoverRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose, triggerRef]);

    // Handle escape key
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;
    if (!hasTestPlan && !hasScenarios) return null;

    // Calculate batch progress for live view
    // A batch is completed if all its scenarios are passed/failed, or if we've moved past it
    const totalBatches = testPlan.length;
    const completedBatches = testPlan.filter((batch, batchIdx) => {
        const allScenariosComplete = batch.scenarios.every(s => s.status === 'passed' || s.status === 'failed');
        const isPastBatch = batchIdx < currentBatchIndex;
        return allScenariosComplete || isPastBatch;
    }).length;

    // Calculate batch progress for recorded view (single batch)
    const recordedBatchTotal = 1;
    const recordedBatchCompleted = scenarios.length > 0 && scenarios.every(s => s.status === 'passed' || s.status === 'failed') ? 1 : 0;

    const renderScenarioIcon = (status: ScenarioItem['status']) => {
        switch (status) {
            case 'passed':
                return <Icon name={IconName.CheckCircle} size="xs" variant="success" />;
            case 'failed':
                return <Icon name={IconName.Close} size="xs" variant="error" />;
            case 'running':
                return <Icon name={IconName.Loading} size="xs" spin variant="success" />;
            default:
                return <div className="w-3 h-3 rounded-full border border-border-secondary bg-transparent" />;
        }
    };

    const getScenarioColor = (status: ScenarioItem['status']) => {
        switch (status) {
            case 'failed':
                return 'error' as const;
            case 'passed':
                return 'success' as const;
            case 'running':
                return 'primary' as const;
            default:
                return 'secondary' as const;
        }
    };

    return createPortal(
        <div
            ref={popoverRef}
            className="fixed"
            style={{
                top: position ? `${position.top}px` : '-9999px',
                left: position ? `${position.left}px` : '-9999px',
                zIndex: 10000,
                visibility: position ? 'visible' : 'hidden',
            }}
        >
            {/* Popover Container */}
            <div className="relative">
                {/* Arrow - position changes based on popover placement */}
                {position && (
                    <div
                        className="absolute z-10 text-bg-primary"
                        style={{
                            ...(position.arrowPosition === 'left' && {
                                left: '-6px',
                                top: `${position.arrowOffset - 8}px`,
                            }),
                            ...(position.arrowPosition === 'right' && {
                                right: '-6px',
                                top: `${position.arrowOffset - 8}px`,
                            }),
                        }}
                    >
                        {position.arrowPosition === 'left' && (
                            <svg width="6" height="16" viewBox="0 0 6 16" fill="currentColor">
                                <path d="M0 8L6 0L6 16L0 8Z" fill="currentColor" />
                            </svg>
                        )}
                        {position.arrowPosition === 'right' && (
                            <svg width="6" height="16" viewBox="0 0 6 16" fill="currentColor">
                                <path d="M6 8L0 0L0 16L6 8Z" fill="currentColor" />
                            </svg>
                        )}
                    </div>
                )}

                {/* Popover Content */}
                <div className="w-96 max-w-[90vw] bg-bg-primary border border-border-secondary rounded-bg-v2-md shadow-[0px_24px_48px_-12px_rgba(10,13,18,0.18),0px_4px_4px_-2px_rgba(10,13,18,0.04)]">
                    {/* Popover Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
                        <div className="flex items-center gap-2">
                            <Text variant="text-sm" weight="semibold" color="primary">
                                {isLive ? 'Test Plan' : 'Test Scenarios'}
                            </Text>
                            <Text variant="text-xs" color="secondary">
                                ({isLive && hasTestPlan ? `${completedBatches}/${totalBatches}` : `${recordedBatchCompleted}/${recordedBatchTotal}`})
                            </Text>
                        </div>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                            className="p-1 hover:bg-bg-tertiary rounded transition-colors cursor-pointer"
                        >
                            <Icon name={IconName.Close} size="xs" variant="secondary" />
                        </button>
                    </div>
                    
                    {/* Content */}
                    <div className="max-h-80 overflow-y-auto p-2 modern-scrollbar-spaced">
                        {isLive && hasTestPlan ? (
                            // Live view: Show all batches with progress
                            testPlan.map((batch, batchIdx) => {
                                const batchCompleted = batch.scenarios.filter(s => s.status === 'passed' || s.status === 'failed').length;
                                const batchTotal = batch.scenarios.length;
                                const isBatchActive = batchIdx === currentBatchIndex;
                                
                                return (
                                    <div key={batchIdx} className="mb-3 last:mb-0">
                                        {/* Batch Header */}
                                        <div className={`flex items-center gap-2 px-2 py-1.5 rounded ${
                                            isBatchActive ? 'bg-green-500/10' : ''
                                        }`}>
                                            <Icon 
                                                name={isBatchActive ? IconName.Loading : IconName.FolderOpen} 
                                                size="xs" 
                                                variant={isBatchActive ? "success" : "secondary"}
                                                spin={isBatchActive}
                                            />
                                            <Text variant="text-xs" weight="medium" color={isBatchActive ? "primary" : "secondary"} className="flex-1">
                                                {batch.batch_name}
                                            </Text>
                                            <Text variant="text-xs" color="secondary">
                                                {batchCompleted}/{batchTotal}
                                            </Text>
                                        </div>
                                        
                                        {/* Scenarios in batch */}
                                        <div className="ml-4 mt-1">
                                            {batch.scenarios.map((scenario, sIdx) => (
                                                <div 
                                                    key={`${batchIdx}-${sIdx}`}
                                                    className={`flex items-start gap-2 px-2 py-1 rounded ${
                                                        scenario.status === 'running' ? 'bg-green-500/10' : ''
                                                    }`}
                                                >
                                                    <div className="mt-0.5 flex-shrink-0">
                                                        {renderScenarioIcon(scenario.status)}
                                                    </div>
                                                    <Text
                                                        variant="text-xs"
                                                        color={getScenarioColor(scenario.status)}
                                                        className={scenario.status === 'running' ? 'font-medium' : ''}
                                                    >
                                                        {scenario.name}
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            // Recorded view: Show only scenarios (no batch name)
                            scenarios.map((scenario, index) => (
                                <div 
                                    key={index}
                                    className={`flex items-start gap-2 px-2 py-1.5 rounded ${
                                        scenario.status === 'running' ? 'bg-green-500/10' : ''
                                    }`}
                                >
                                    <div className="mt-0.5 flex-shrink-0">
                                        {renderScenarioIcon(scenario.status)}
                                    </div>
                                    <Text
                                        variant="text-xs"
                                        color={getScenarioColor(scenario.status)}
                                        className={scenario.status === 'running' ? 'font-medium' : ''}
                                    >
                                        {scenario.name}
                                    </Text>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TestPlanPopover;
